import { AlertTriangle, ArrowRight, BookOpen, Check, ChevronLeft, Circle, Clock3, EyeOff, FileCheck2, LoaderCircle, MessageSquareText, Send, Sparkles, X } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { api, postJson } from "../api";
import { createPortal } from "react-dom";
import { useDialogLifecycle } from "../dialog";
import { confirmDiscard, useUnsavedChanges } from "../interaction";
import { choiceKeyDown } from "../keyboard";
import type { BookConsultation, BookConsultationMode, Project } from "../types";
import { StatusDot } from "./Ui";

const fields = [
  ["title", "书名与备选名"],
  ["genre", "题材与目标读者"],
  ["hook", "核心卖点与体验"],
  ["protagonist", "主角与成长"],
  ["world", "世界规则"],
  ["mechanism", "核心机制"],
  ["conflict", "主线冲突"],
  ["relationships", "人物关系"],
  ["ending", "结局方向"],
  ["scale", "篇幅与节奏"],
  ["style", "视角与文风边界"],
  ["boundaries", "创作与版权禁区"]
] as const;

const spoilerFreeFields = [
  ["hook", "核心设想与阅读体验"],
  ["protagonist", "主角起点与主要欲望"],
  ["scale", "大致篇幅"],
  ["boundaries", "必须保留或避开的内容"]
] as const;

interface NewBookStudioProps {
  open: boolean;
  sourceProject: Project;
  onClose: () => void;
  onProjectCreated: (projectId: string) => Promise<void>;
  notify: (message: string, tone?: "success" | "error" | "info") => void;
}

export function NewBookStudio({ open, sourceProject, onClose, onProjectCreated, notify }: NewBookStudioProps) {
  const [consultation, setConsultation] = useState<BookConsultation | null>(null);
  const [recent, setRecent] = useState<BookConsultation[]>([]);
  const [brief, setBrief] = useState("");
  const [mode, setMode] = useState<BookConsultationMode>("spoiler_free");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [entering, setEntering] = useState(false);
  const chatEnd = useRef<HTMLDivElement | null>(null);
  const requestLock = useRef(false);
  const [historyError, setHistoryError] = useState("");
  const [historyReload, setHistoryReload] = useState(0);
  const [syncError, setSyncError] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const dialogRef = useDialogLifecycle(open, onClose, busy);
  useUnsavedChanges(Boolean(brief.trim() || message.trim()), "新书想法或未发送的讨论消息", busy && !entering);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController(); setHistoryError("");
    api<{ consultations: BookConsultation[] }>("/api/book-consultations", { signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) setRecent(result.consultations.filter((item) => item.status !== "completed")); })
      .catch((error) => { if (!controller.signal.aborted) setHistoryError(error instanceof Error ? error.message : "讨论记录加载失败"); });
    return () => controller.abort();
  }, [open, sourceProject.id, historyReload]);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [consultation?.messages.length]);

  useEffect(() => {
    if (!open || !consultation || (consultation.status !== "generating" && !consultation.replyPending)) return;
    const controller = new AbortController(); let inFlight = false;
    const refresh = async () => {
      if (inFlight || controller.signal.aborted) return; inFlight = true;
      try {
        const next = await api<BookConsultation>(`/api/book-consultations/${consultation.id}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setConsultation(next); setSyncError("");
        if (next.status === "completed") notify("新书账本和第一卷逐章简介已生成", "success");
        if (next.status === "failed") notify(next.error || "新书生成失败", "error");
      } catch (error) { if (!controller.signal.aborted) setSyncError(error instanceof Error ? error.message : "进度连接中断，正在重试"); }
      finally { inFlight = false; }
    };
    void refresh(); const timer = window.setInterval(() => void refresh(), 3000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [open, consultation?.id, consultation?.status, consultation?.replyPending]);

  const confirmedKeys = useMemo(() => new Set(consultation?.confirmedFacts.map((fact) => fact.field) ?? []), [consultation?.confirmedFacts]);
  const visibleFields = consultation?.mode === "spoiler_free" ? spoilerFreeFields : fields;
  const canGenerate = Boolean(consultation && !consultation.replyPending && (consultation.mode === "spoiler_free"
    ? consultation.missingFields.length === 0
    : consultation.readiness >= 65));
  if (!open) return null;

  async function startDiscussion(event: FormEvent) {
    event.preventDefault();
    if (requestLock.current || brief.trim().length < 10) return;
    requestLock.current = true;
    setBusy(true);
    try {
      const next = await postJson<BookConsultation>("/api/book-consultations", { sourceProjectId: sourceProject.id, brief, mode });
      setConsultation(next);
      setBrief("");
    } catch (error) {
      notify(error instanceof Error ? error.message : "新书讨论启动失败", "error");
    } finally {
      requestLock.current = false;
      setBusy(false);
    }
  }

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    if (!consultation || !message.trim() || requestLock.current || consultation.replyPending || uncertain || ["generating", "completed"].includes(consultation.status)) return;
    requestLock.current = true;
    const text = message.trim();
    const requestId = crypto.randomUUID();
    setMessage("");
    setBusy(true);
    try {
      const optimistic = { ...consultation, messages: [...consultation.messages, { id: `local-${Date.now()}`, role: "user" as const, content: text, createdAt: new Date().toISOString() }] };
      setConsultation(optimistic);
      const next = await postJson<BookConsultation>(`/api/book-consultations/${consultation.id}/messages`, { message: text, requestId });
      setConsultation(next);
    } catch (error) {
      try {
        const latest = await api<BookConsultation>(`/api/book-consultations/${consultation.id}`);
        setConsultation(latest);
        const accepted = latest.messages.slice(consultation.messages.length).some((item) => item.role === "user" && item.content === text);
        if (!accepted) setMessage(text);
      } catch { setConsultation(consultation); setMessage(text); setUncertain(true); setSyncError("暂时无法确认消息是否已经收到。输入已保留，请先刷新讨论状态，再决定是否重发。"); }
      notify(error instanceof Error ? error.message : "消息发送失败，输入已保留", "error");
    } finally {
      requestLock.current = false;
      setBusy(false);
    }
  }

  async function generateBook() {
    if (!consultation || requestLock.current || consultation.replyPending || consultation.status === "generating" || consultation.status === "completed" || (consultation.status !== "failed" && !canGenerate)) return;
    if (message.trim()) { notify("请先发送或清除输入框中的补充设定，再开始生成", "info"); return; }
    requestLock.current = true;
    setBusy(true);
    try {
      const next = await postJson<BookConsultation>(`/api/book-consultations/${consultation.id}/finalize`);
      setConsultation(next);
      notify("主 Agent 正在整理正典，随后将分批生成第一卷逐章简介", "info");
    } catch (error) {
      notify(error instanceof Error ? error.message : "账本生成启动失败", "error");
    } finally {
      requestLock.current = false;
      setBusy(false);
    }
  }

  async function openDiscussion(item: BookConsultation) {
    if (requestLock.current || !await confirmDiscard(Boolean(brief.trim() || message.trim()), "未发送的新书输入")) return;
    requestLock.current = true; setBusy(true);
    try {
      const latest = await api<BookConsultation>(`/api/book-consultations/${item.id}`);
      setConsultation(latest); setBrief(""); setMessage(""); setUncertain(false); setSyncError("");
    } catch (error) { notify(error instanceof Error ? error.message : "讨论加载失败，当前输入仍保留", "error"); }
    finally { requestLock.current = false; setBusy(false); }
  }
  async function refreshDiscussion() {
    if (!consultation || requestLock.current) return;
    requestLock.current = true; setBusy(true);
    try {
      const latest = await api<BookConsultation>(`/api/book-consultations/${consultation.id}`);
      if (uncertain && latest.messages.slice(consultation.messages.length).some((item) => item.role === "user" && item.content === message.trim())) setMessage("");
      setConsultation(latest); setUncertain(false); setSyncError("");
    } catch (error) { setSyncError(error instanceof Error ? error.message : "状态刷新失败，输入仍保留"); }
    finally { requestLock.current = false; setBusy(false); }
  }
  async function enterProject() {
    if (!consultation?.projectId || requestLock.current) return;
    requestLock.current = true; setEntering(true); setBusy(true);
    try { await onProjectCreated(consultation.projectId); }
    catch (error) { notify(error instanceof Error ? error.message : "进入新书失败，请重试", "error"); }
    finally { requestLock.current = false; setEntering(false); setBusy(false); }
  }
  async function reset() {
    if (requestLock.current || !await confirmDiscard(Boolean(brief.trim() || message.trim()), "未发送的新书输入")) return;
    setHistoryReload((value) => value + 1); setSyncError(""); setUncertain(false);
    setConsultation(null);
    setBrief("");
    setMode("spoiler_free");
    setMessage("");
  }

  return createPortal(
    <div ref={(node) => { dialogRef.current = node; }} tabIndex={-1} className="new-book-overlay" aria-busy={busy} role="dialog" aria-modal="true" aria-label="新建小说">
      <header className="new-book-header">
        <div className="new-book-brand"><span><Sparkles size={18} /></span><div><strong>新书主 Agent</strong><small>先确定方向 · 开篇试写后滚动展开</small></div></div>
        <div className="new-book-header-status">{consultation && <><StatusDot status={consultation.status === "completed" ? "completed" : consultation.status === "failed" ? "failed" : consultation.status === "generating" ? "running" : "warning"} /><span>{statusLabel(consultation.status)}</span></>}</div>
        <button className="icon-button" type="button" disabled={busy} onClick={onClose} title="关闭，当前讨论保留" aria-label="关闭新书讨论"><X size={18} /></button>
      </header>

      {!consultation ? <main className="new-book-start">
        <StepNavigation active="concept" />
        <section className="new-book-start-main" id="new-book-step-concept">
          <span className="start-icon"><BookOpen size={25} /></span>
          <h1>从一个想法开始</h1>
          <form onSubmit={startDiscussion}>
            <label>讨论方式</label>
            <div className="new-book-mode-control" role="radiogroup" aria-label="新书讨论方式" onKeyDown={choiceKeyDown}>
              <button type="button" disabled={busy} role="radio" aria-checked={mode === "spoiler_free"} tabIndex={mode === "spoiler_free" ? 0 : -1} className={mode === "spoiler_free" ? "active" : ""} onClick={() => setMode("spoiler_free")}><EyeOff size={16} /><span><strong>防剧透</strong><small>只确认4项核心授权</small></span></button>
              <button type="button" disabled={busy} role="radio" aria-checked={mode === "guided"} tabIndex={mode === "guided" ? 0 : -1} className={mode === "guided" ? "active" : ""} onClick={() => setMode("guided")}><MessageSquareText size={16} /><span><strong>深度共创</strong><small>逐项讨论完整设定</small></span></button>
            </div>
            <label htmlFor="book-brief">你想写一本什么样的书？</label>
            <textarea data-autofocus disabled={busy} minLength={10} maxLength={20000} id="book-brief" onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} value={brief} onChange={(event) => setBrief(event.target.value)} placeholder="可以很模糊，例如：我想写一本近未来悬疑，主角能看见别人遗忘的记忆，但每次使用都会失去自己的一段过去。" autoFocus />
            <p className="ux-live-status" role="status">{brief.trim().length < 10 ? `至少描述 10 字，还需 ${10 - brief.trim().length} 字。` : "描述已满足长度要求；Ctrl+Enter / ⌘Enter 提交。"}</p>
            <button className="button button-primary" type="submit" disabled={busy || brief.trim().length < 10}>{busy ? <span className="spinner" /> : mode === "spoiler_free" ? <EyeOff size={16} /> : <MessageSquareText size={16} />}{busy ? "主 Agent 正在思考" : mode === "spoiler_free" ? "确认核心想法" : "开始讨论"}</button>
          </form>
          {historyError && <div className="ux-error" role="alert"><p>{historyError}</p><button type="button" className="button button-secondary" disabled={busy} onClick={() => setHistoryReload((value) => value + 1)}>重试加载讨论记录</button></div>}
          {recent.length > 0 && <div className="recent-consultations"><h2>继续未完成的讨论</h2>{recent.map((item) => <button type="button" key={item.id} disabled={busy} onClick={() => void openDiscussion(item)}><span><strong>{item.workingTitle || "未命名新书"}</strong><small>{consultationModeLabel(item.mode)} · {consultationHistoryLabel(item.status)} · {item.messages.length} 条消息 · 完成度 {item.readiness}%</small></span><ArrowRight size={15} /></button>)}</div>}
        </section>
        <aside className="new-book-template-summary"><h2>生成内容</h2><div>{["系列承诺与世界规则", "人物、关系与势力账本", "伏笔、支线与时间线", "分卷总纲与第一卷事件", "新书视角与文风硬边界", "开篇章纲先就绪，后续滚动规划"].map((item) => <span key={item}><Check size={14} />{item}</span>)}</div><small>仅保留架构方法，不复用具体人名与情节。</small></aside>
      </main> : <main className="new-book-studio-grid">
        <StepNavigation active={consultationStep(consultation)} />
        <section className="consultation-chat" id="new-book-step-concept">
          <header><button type="button" className="icon-button" title="返回讨论列表" disabled={busy} onClick={() => void reset()}><ChevronLeft size={17} /></button><div><strong>{consultation.workingTitle || "未命名新书"}</strong><small>{consultation.model} · 主 Agent · {consultationModeLabel(consultation.mode)}</small></div></header>
          <div className="consultation-messages">{consultation.messages.map((item) => <article key={item.id} className={`message-${item.role}`}><span>{item.role === "user" ? "作者" : "主"}</span><div>{item.content}</div></article>)}{busy && <article className="message-assistant thinking"><span>主</span><div><LoaderCircle size={16} className="spin-icon" />正在整理你的选择</div></article>}<div ref={chatEnd} /></div>
          {syncError && <p className="ux-error" role="alert">{syncError}</p>}
          <div className="ux-live-status">Ctrl+Enter / ⌘Enter 发送；普通 Enter 换行。<button type="button" className="button button-ghost" disabled={busy} onClick={() => void refreshDiscussion()}>刷新讨论状态</button></div>
          <form className="consultation-input" onSubmit={sendMessage}><textarea aria-label="新书讨论消息" maxLength={20000} onKeyDown={(event) => { if ((event.ctrlKey || event.metaKey) && event.key === "Enter" && !event.nativeEvent.isComposing) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} value={message} onChange={(event) => setMessage(event.target.value)} placeholder="回答主 Agent，或补充你想到的设定" disabled={busy || consultation.replyPending || consultation.status === "generating" || consultation.status === "completed"} /><button type="submit" className="icon-button" title={uncertain ? "请先刷新讨论状态" : "发送消息"} aria-label="发送新书讨论消息" disabled={busy || consultation.replyPending || uncertain || !message.trim() || consultation.status === "generating" || consultation.status === "completed"}><Send size={17} /></button></form>
        </section>

        <aside className="consultation-ledger">
          <header id="new-book-step-style"><span className="section-kicker">CANON READINESS</span><h2>{consultation.mode === "spoiler_free" ? "核心授权" : "创作决策"}</h2></header>
          <div className="readiness-score"><div><strong>{consultation.readiness}%</strong><span>{consultation.missingFields.length} 项待确认</span></div><div><span style={{ width: `${consultation.readiness}%` }} /></div></div>
          <div className="decision-checklist" id="new-book-step-structure">{visibleFields.map(([key, label]) => { const done = confirmedKeys.has(key) && !consultation.missingFields.includes(key); return <div key={key} className={done ? "done" : ""}><span>{done ? <Check size={12} /> : null}</span><strong>{label}</strong></div>; })}</div>
          {consultation.confirmedFacts.length > 0 && <div className="confirmed-facts"><h3>已确认</h3>{consultation.confirmedFacts.map((fact) => <div key={fact.field} className={`confirmed-fact fact-${fact.field}`}><span>{fieldLabel(fact.field)}</span><p>{fact.value}</p></div>)}</div>}
          <div className="consultation-finalize" id="new-book-step-team">
            <p className="ux-note">{consultation.status === "generating" ? "生成任务已交给服务端。关闭后可从未完成讨论继续查看，不必重复启动。" : !canGenerate ? `还有 ${consultation.missingFields.length} 项授权或设定待确认，请继续回答左侧问题。` : message.trim() ? "还有未发送的补充设定，请先发送或清除，再开始生成。" : "生成将使用模型并创建独立的新书工程，不会覆盖当前小说。"}</p>
            {consultation.status === "generating" || consultation.status === "failed" || consultation.status === "completed" ? <GenerationPanel busy={busy} consultation={consultation} onOpenProject={() => void enterProject()} onRetry={() => void generateBook()} /> : <button className="button button-primary" type="button" onClick={generateBook} disabled={busy || !canGenerate || Boolean(message.trim()) || uncertain}><Sparkles size={16} />{consultation.mode === "spoiler_free" ? "交给 Agent 完成创作设计" : "形成账本并规划第一卷"}</button>}
          </div>
        </aside>
      </main>}
    </div>, document.body
  );
}

function GenerationPanel({ busy, consultation, onOpenProject, onRetry }: { busy: boolean; consultation: BookConsultation; onOpenProject: () => void; onRetry: () => void }) {
  const generation = consultation.generation;
  return <section className={`generation-panel generation-${generation.stage}`} id="new-book-step-cover">
    <header><div><span className="section-kicker">GENERATION PIPELINE</span><h3>{generation.stage === "completed" ? "新书已准备完成" : generation.stage === "failed" ? "生成流程中断" : "正在生成正典与逐章简介"}</h3></div><strong>{generation.progress}%</strong></header>
    <div className="generation-bar"><span style={{ width: `${generation.progress}%` }} /></div>
    <p className="generation-detail">{generation.detail}</p>
    {generation.stage === "failed" && <button className="button button-primary generation-continue" type="button" disabled={busy} onClick={onRetry}><Sparkles size={15} />从断点继续生成</button>}
    <div className="generation-metrics"><span><small>逐章简介</small><strong>{generation.plannedChapters} / {generation.totalPlanChapters || "--"}</strong></span><span><small>当前批次</small><strong>{generation.currentBatch || "--"} / {generation.totalBatches || "--"}</strong></span><span><small>账本文档</small><strong>{generation.documentsGenerated} / {generation.totalDocuments}</strong></span></div>
    <div className="generation-steps">{generation.steps.map((step) => <div key={step.id} className={`generation-step step-${step.status}`}><span>{step.status === "completed" ? <Check size={12} /> : step.status === "running" ? <LoaderCircle size={13} className="spin-icon" /> : step.status === "failed" ? <AlertTriangle size={13} /> : <Circle size={11} />}</span><div><strong>{step.label}</strong><small>{step.detail}</small></div></div>)}</div>
    {generation.log.length > 0 && <details className="generation-log" open={generation.stage === "failed"}><summary><Clock3 size={13} />最近执行记录</summary><div>{generation.log.slice(-8).reverse().map((item, index) => <p key={`${item.timestamp}-${index}`}><time>{new Date(item.timestamp).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</time><span>{item.message}</span></p>)}</div></details>}
    {generation.stage === "completed" && consultation.projectId && <button className="button button-primary generation-action" type="button" disabled={busy} onClick={onOpenProject}><FileCheck2 size={16} />进入新书创作台</button>}
  </section>;
}

type StudioStep = "concept" | "style" | "structure" | "team" | "cover";
const studioSteps: Array<{ id: StudioStep; label: string }> = [
  { id: "concept", label: "概念" },
  { id: "style", label: "授权" },
  { id: "structure", label: "结构" },
  { id: "team", label: "生成" },
  { id: "cover", label: "就绪" }
];

function StepNavigation({ active }: { active: StudioStep }) {
  const activeIndex = studioSteps.findIndex((step) => step.id === active);
  return <aside className="new-book-steps" aria-label="新书创建步骤">
    <span className="section-kicker">NEW BOOK</span>
    <h2>创作向导</h2>
    <ol>{studioSteps.map((step, index) => <li key={step.id} className={`${index === activeIndex ? "active" : ""} ${index < activeIndex ? "completed" : ""}`}><button type="button" disabled={index > activeIndex} aria-current={index === activeIndex ? "step" : undefined} onClick={() => document.getElementById(`new-book-step-${step.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}><span>{index < activeIndex ? <Check size={13} /> : String(index + 1).padStart(2, "0")}</span>{step.label}</button></li>)}</ol>
    <p>确定核心设定，生成全书骨架。</p>
  </aside>;
}

function consultationStep(consultation: BookConsultation): StudioStep {
  if (consultation.status === "completed" || consultation.generation.stage === "project") return "cover";
  if (consultation.status === "generating") return consultation.generation.stage === "plans" || consultation.generation.stage === "documents" ? "team" : "structure";
  if (consultation.readiness >= 75) return "structure";
  if (consultation.readiness >= 45) return "style";
  return "concept";
}

function fieldLabel(key: string): string {
  return spoilerFreeFields.find((field) => field[0] === key)?.[1] ?? fields.find((field) => field[0] === key)?.[1] ?? key;
}
function consultationModeLabel(mode: BookConsultationMode): string {
  return mode === "spoiler_free" ? "防剧透" : "深度共创";
}
function consultationHistoryLabel(status: BookConsultation["status"]): string {
  return ({ discussing: "讨论中", ready: "待生成", generating: "生成中", completed: "已完成", failed: "生成中断，可继续" })[status];
}
function statusLabel(status: BookConsultation["status"]): string {
  return ({ discussing: "讨论中", ready: "可生成账本", generating: "生成账本中", completed: "新书已就绪", failed: "生成中断" })[status];
}
